import { faBirthdayCake, faEllipsis, faUserCheck, faUserClock, faUserXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ExportTnAExtraHoursToExcel from "./ExportTnAExtraHoursToExcel/ExportTnAExtraHoursToExcel";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { RefModel } from "../../../models/base/ref-model";
import { TnAExtraHoursOverviewDTO } from "../../../models/tna-extra-hours-overviews/tna-extra-hours-overview-dto";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../../reusable-components/Table/Table";
import { TnAExtraHoursOverviewsApiService } from "../../../services/tna-extra-hours-overviews/tna-extra-hours-overviews-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { getDayAndMonth, getDaysInMonth, intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { Typography } from "../../WithHintOverlay/styles";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";
import {
  ClientIconContainer,
  EmployeeNameContainer,
  ExtraHoursContainer,
  MainTableContainer,
  StatusIndicator,
} from "./styles";
import { EmployeeDTO } from "../../../models/employees";
import { TnADayDataRef } from "../../../models/tna/tna-data/tna-day-data-ref";
import { TnAExtraHoursRef } from "../../../models/tna/tna-extra-hours/tna-extra-hours-ref";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";

interface State {
  loading: boolean;
  error: string;
}

const TnAExtraHours = () => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_extra_hours";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const theme = useTheme();

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const {
    legalEntity: { legalEntityOption, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);

  const navigate = useNavigate();

  const allItems = useRef<TnAExtraHoursOverviewDTO[]>([]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  const clickNavigate = (employee: EmployeeDTO) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const columns: GridColDef[] = [
    // {
    //   field: "employee.name",
    //   renderCell: ({ row }) => (
    //     <ConcatenatedCell
    //       aligned={"left"}
    //       FirstElement={
    //         <EmployeeNameContainer>
    //           <span
    //             title={row?.employee?.name}
    //             onClick={() => {
    //               clickNavigate(row.employee);
    //             }}
    //           >
    //             {row?.employee?.name}
    //           </span>
    //         </EmployeeNameContainer>
    //       }
    //       SecondElement={
    //         <Grid>
    //           <Typography
    //             style={{ color: theme.palette.text.disabled, padding: "0px" }}
    //             variant={"body2"}
    //             title={row?.employee?.legalEntity?.name}
    //           >
    //             {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
    //           </Typography>
    //         </Grid>
    //       }
    //     />
    //   ),
    //   headerName: `${t(`common_words.employee`)} | ${t(`common_words.legal_entity`)}`,
    //   headerAlign: "center",
    //   flex: 1,
    //   minWidth: 150,
    // },
    {
      field: "employee.employeeCode, employee.internalId",
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography style={{ padding: "0px" }}>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled, padding: "0px" }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography
                    variant={"body2"}
                    sx={{ padding: 0 }}
                  >
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled, padding: 0 }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Typography>{`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      headerName: t("common_words.tna_employee_group"),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "extraHoursYTD",
      valueGetter: ({ row }) => `${row.extraHoursYTD ? row.extraHoursYTD.toFixed(2) : ""}`,
      headerName: t(`${translationFields}.ytd`),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "balance",
      renderCell: ({ row }) => {
        const balance = row.daysData
          ?.map((day: TnADayDataRef) =>
            day?.extraHours?.reduce((acc, curr) => acc + (!curr.isRejected && !curr.isCancelled ? curr?.amount : 0), 0)
          )
          .reduce((acc: number, curr: number) => acc + curr, 0);

        return `${balance ? balance.toFixed(2) : ""}`;
      },
      headerName: t(`${translationFields}.balance`),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const dynamicColumns = (year: number, month: number) => {
    const result: GridColDef[] = [];
    const daysInMonth = getDaysInMonth(year, month);
    for (let i = 0; i < daysInMonth; i++) {
      result.push({
        field: new Date(year, month, i + 1).toString(),
        renderCell: ({ row }) => {
          const currDay = row?.daysData?.find((day: TnADayDataRef) => day?.tnADate?.day === i + 1);
          const extraHoursTime = currDay?.extraHours?.reduce(
            (acc: number, curr: TnAExtraHoursRef) => acc + (!curr.isRejected && !curr.isCancelled ? curr?.amount : 0),
            0
          );

          const isWeekend = [0, 6].indexOf(new Date(year, month, i + 1).getDay()) != -1;

          return (
            !isWeekend &&
            !!extraHoursTime && (
              <ExtraHoursContainer
                title={`Total ${extraHoursTime < 0 ? "-" : ""}${Number(extraHoursTime).toFixed(2)}h`}
              >
                {currDay?.extraHours?.map((eh: TnAExtraHoursRef, idx: number) => {
                  if (idx > 0) {
                    return;
                  } else {
                    return (
                      <StatusIndicator
                        isRejected={eh.isRejected}
                        isCancelled={eh.isCancelled}
                        isApproved={eh.isApproved}
                        key={eh.id}
                      >
                        <ClientIconContainer>
                          <React.Fragment key={idx}>
                            {`${eh.amount < 0 ? "-" : ""}${Number(eh.amount).toFixed(2)}h${
                              !eh.isRejected && !eh.isCancelled && !eh.isApproved ? "?" : ""
                            }`}
                            {eh?.needAcceptance &&
                              (eh?.acceptedByClient ? (
                                <FontAwesomeIcon
                                  icon={faUserCheck}
                                  color={theme.palette.success.main}
                                />
                              ) : eh?.rejectedByClient ? (
                                <FontAwesomeIcon
                                  icon={faUserXmark}
                                  color={theme.palette.error.main}
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon={faUserClock}
                                  color={theme.palette.text.secondary}
                                />
                              ))}
                          </React.Fragment>
                        </ClientIconContainer>
                      </StatusIndicator>
                    );
                  }
                })}

                {currDay?.extraHours?.length > 1 && (
                  <SimplePopover
                    buttonType="icon"
                    buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
                    flow="column"
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    {currDay?.extraHours?.slice(1).map((eh: TnAExtraHoursRef, idx: number) => {
                      return (
                        <div
                          style={{
                            marginLeft: "3px",
                            marginRight: "3px",
                            marginBottom: "2px",
                            marginTop: "2px",
                          }}
                          key={idx}
                        >
                          <StatusIndicator
                            isRejected={eh.isRejected}
                            isCancelled={eh.isCancelled}
                            isApproved={eh.isApproved}
                            key={eh.id}
                          >
                            <ClientIconContainer>
                              {`${eh.amount < 0 ? "-" : ""}${Number(eh.amount).toFixed(2)}h${
                                !eh.isRejected && !eh.isCancelled && !eh.isApproved ? "?" : ""
                              }`}
                              <React.Fragment key={idx}>
                                {eh?.needAcceptance &&
                                  (eh?.acceptedByClient ? (
                                    <FontAwesomeIcon
                                      icon={faUserCheck}
                                      color={"greenyellow"}
                                    />
                                  ) : eh?.rejectedByClient ? (
                                    <FontAwesomeIcon
                                      icon={faUserXmark}
                                      color={theme.palette.error.main}
                                    />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={faUserClock}
                                      color={theme.palette.text.secondary}
                                    />
                                  ))}
                              </React.Fragment>
                            </ClientIconContainer>
                          </StatusIndicator>
                        </div>
                      );
                    })}
                  </SimplePopover>
                )}
              </ExtraHoursContainer>
            )
          );
        },
        headerName: getDayAndMonth(new Date(year, month, i + 1)),
        headerAlign: "center",
        align: "center",
        flex: 1,
        minWidth: 80,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params: any) => {
          const currDay = new Date(params.field);
          const dayOfWeek: number = currDay.getDay();
          const thisDayData = params.row?.daysData?.find(
            (day: TnADayDataRef) => day?.tnADate?.day === currDay.getDate()
          );

          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return thisDayData?.isPublicHoliday ? "isPublicHoliday" : isWeekend ? "isWeekend" : "";
        },
      });
    }

    return result;
  };

  const defaultItem: Partial<TnAExtraHoursOverviewDTO> = {
    employee: null,
    daysData: [],
  };

  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnAExtraHoursData = useCallback(
    async (pageSize = 10) => {
      try {
        const response = await new TnAExtraHoursOverviewsApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year,
            month: month + 1,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      year,
      month,
    ]
  );

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            {!!legalEntityQuickFilter && legalEntityQuickFilter}
            {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
            {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
            {!!yearMonthQuickFilter && yearMonthQuickFilter}
            <ExportTnAExtraHoursToExcel
              loadEmployeeGroup={loadEmployeeGroup}
              loadTnAExtraHoursData={loadTnAExtraHoursData}
              disabled={state.loading || loading || !items?.length}
              allItems={() => allItems.current}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [state.loading, legalEntityQuickFilter, employeeGroupQuickFilter, onlyCurrentEmployeesQuickFilter]
  );

  return (
    <LoadingOverlay
      loading={state.loading}
      error={state.error}
    >
      <MainTableContainer>
        {(hasAccess || allowedToViewOnly) && (
          <Table
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new TnAExtraHoursOverviewsApiService()}
            propsFilter={
              {
                ...legalEntityOption,
                ...employeeGroupOption,
                ...onlyCurrentEmployeesOptionValue,
                year,
                month: month + 1,
              } as TnAOverviewsFilter
            }
            defaultItem={defaultItem}
            filter={FT_MTPAL}
            readOnly
            quickFilters={quickfilters}
            dynamicColumns={dynamicColumns(year, month)}
            getAllItems={(items) => {
              allItems.current = items as TnAExtraHoursOverviewDTO[];
            }}
            pinnedColumns={{
              left: ["employee.employeeCode, employee.internalId", "employee.name"],
            }}
          />
        )}
      </MainTableContainer>
    </LoadingOverlay>
  );
};

export default TnAExtraHours;
